import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import { client, useHubLogin } from '../../session';

/**
 * Billing reports through the hub (`/billing/…`, `Billing/BillingEndpoints.cs`). The hub fetches each service's usage
 * with its own credential and keeps every run (the report JSON), so a run renders and sends the same numbers again.
 */

export interface BillingTarget {
  id: string;
  label: string;
  /** The hub holds a credential for this report's service in that environment. */
  configured: boolean;
}

export interface BillingReportInfo {
  id: string;
  title: string;
  product: string;
  description: string;
  targets: BillingTarget[];
}

export interface BillingCatalog {
  mailConfigured: boolean;
  reports: BillingReportInfo[];
}

export interface BillingColumn {
  key: string;
  label: string;
  format: 'int' | 'money';
}

export type Values = Record<string, number>;

export interface BillingCustomer {
  id: string;
  name: string;
  internal: boolean;
  lines: { label: string; values: Values }[];
  total: Values;
}

export interface BillingDetailRow {
  customerId: string;
  customer: string;
  internal: boolean;
  installation: string;
  line: string;
  values: Values;
}

export interface BillingResult {
  reportId: string;
  title: string;
  product: string;
  target: string;
  targetLabel: string;
  from: string;
  to: string;
  periodLabel: string;
  generatedAt: string;
  unitKey: string;
  customerLabel: string;
  detailLabel: string;
  columns: BillingColumn[];
  customers: BillingCustomer[];
  total: Values;
  internalTotal: Values;
  detail: BillingDetailRow[];
}

export type RunStatus = 'running' | 'succeeded' | 'failed';

export interface BillingRun {
  id: number;
  reportId: string;
  target: string;
  /** Period [from, to): `to` is exclusive. */
  from: string;
  to: string;
  periodLabel: string;
  scheduleId: string | null;
  status: RunStatus;
  error: string | null;
  createdBy: string;
  createdAt: string;
  finishedAt: string | null;
  recipients: string[];
  sentAt: string | null;
  sendError: string | null;
  /** Only on a single run. */
  result: BillingResult | null;
}

/** A manual run's answer: `id` null = not saved (preview only: no history, no sending). */
export type ManualRun = Omit<BillingRun, 'id'> & { id: number | null };

export type PeriodKind = 'previousMonth' | 'previousWeek';

export interface BillingSchedule {
  id: string;
  name: string;
  reportId: string;
  target: string;
  periodKind: PeriodKind;
  cron: string;
  timezone: string;
  recipients: string[];
  enabled: boolean;
  nextRunAt: string | null;
  /** A failed slot is retried at this time (`attempts` so far). */
  retryAt: string | null;
  attempts: number;
  lastRunAt: string | null;
  lastRunId: number | null;
  updatedAt: string;
  updatedBy: string;
}

export type ScheduleInput = Pick<
  BillingSchedule,
  'name' | 'reportId' | 'target' | 'periodKind' | 'cron' | 'timezone' | 'recipients' | 'enabled'
>;

/** `month` (yyyy-MM) or `from`/`to` (yyyy-MM-dd, `to` inclusive). */
export type PeriodInput = { month: string } | { from: string; to: string };

export type FileFormat = 'pdf' | 'xlsx';

const enc = encodeURIComponent;

export const billing = {
  catalog: () => client.get<BillingCatalog>('/billing/reports'),
  run: (reportId: string, target: string, period: PeriodInput, save = true) =>
    client.post<ManualRun>(`/billing/reports/${enc(reportId)}/run`, { target, ...period, save }),
  runs: (q: { reportId?: string; scheduleId?: string; limit?: number; before?: number } = {}) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v != null && v !== '') p.set(k, String(v));
    const s = p.toString();
    return client.get<BillingRun[]>(`/billing/runs${s ? `?${s}` : ''}`);
  },
  getRun: (id: number) => client.get<BillingRun>(`/billing/runs/${id}`),
  /** A saved run's file, or an unsaved run's rendered from its report. */
  file: async (run: number | BillingResult, format: FileFormat) => {
    const r =
      typeof run === 'number'
        ? await client.requestRaw('GET', `/billing/runs/${run}/file?format=${format}`, undefined)
        : await client.requestRaw('POST', `/billing/render?format=${format}`, JSON.stringify(run));
    const name = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(
      r.headers.get('content-disposition') ?? ''
    )?.[1];
    return { blob: await r.blob(), name: name ? decodeURIComponent(name) : `billing.${format}` };
  },
  deleteRun: (id: number) => client.del<void>(`/billing/runs/${id}`),
  send: (id: number, recipients: string[]) =>
    client.post<BillingRun>(`/billing/runs/${id}/send`, { recipients }),
  schedules: () => client.get<BillingSchedule[]>('/billing/schedules'),
  saveSchedule: (id: string, body: ScheduleInput) =>
    client.put<BillingSchedule>(`/billing/schedules/${enc(id)}`, body),
  deleteSchedule: (id: string) => client.del<void>(`/billing/schedules/${enc(id)}`),
  runSchedule: (id: string) => client.post<BillingRun>(`/billing/schedules/${enc(id)}/run`),
  testMail: (recipients: string[]) => client.post<void>('/billing/mail/test', { recipients }),
};

/** Saves a blob as a file (downloads go through fetch with the bearer, never a URL). */
export function saveBlob(blob: Blob, fileName: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

export async function download(run: number | BillingResult, format: FileFormat) {
  const f = await billing.file(run, format);
  saveBlob(f.blob, f.name);
}

/** Whether the operator may schedule and send (`ophub:billing:write`). */
export function useBillingWrite(): boolean {
  return hasScope(useHubLogin()?.scopes, SCOPES.billingWrite);
}
